import { Currencies, WalletTypes } from '@constants/enums';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectBusiness,
  selectProfileData,
} from '@pages/profile/store/selectors';
import { getCurrentWallet } from '@utils/multi-wallets';
import { selectWalletsData } from '@/modules/wallets/selectors';
import useCopyToClipboard from './useCopyToClipboard';
import useHandleInstructions from './useHandleInstructions';
import useDefaultCurrency from './useDefaultCurrency';
import { UseNewAddFundsModalProps, UseNewAddFundsModalReturn } from '../types';

const useNewAddFundsModal = ({
  initCurrency,
  initWalletType = WalletTypes.GLOBAL_ACCOUNT,
}: Omit<
  UseNewAddFundsModalProps,
  'open' | 'onClose'
>): UseNewAddFundsModalReturn => {
  const { isCopied, values, copyToClipboard } = useCopyToClipboard();

  const business = useSelector(selectBusiness);
  const walletsState = useSelector(selectWalletsData);
  const userProfile = useSelector(selectProfileData);
  const defaultCurrency = useDefaultCurrency();
  const { isAwActive } = userProfile || {};

  const [selectedCurrency, setSelectedCurrency] = useState<Currencies>(
    initCurrency || defaultCurrency
  );
  const [selectedWalletType, setSelectedWalletType] =
    useState<WalletTypes>(initWalletType);

  const wallet = useMemo(
    () =>
      getCurrentWallet(
        walletsState || [],
        selectedCurrency,
        selectedWalletType
      ),
    [walletsState, selectedCurrency, selectedWalletType]
  );

  const isGlobalAccount = useMemo(
    () => wallet?.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT,
    [wallet]
  );

  const { loading, instructions, currency, setCurrency, setWalletId } =
    useHandleInstructions({
      initCurrency: selectedCurrency,
      initWalletId: wallet?.wallet.id,
    });

  const handleOnCurrencyChange = (walletUuid: string) => {
    const selectedWallet = walletsState?.find((w) => w.uuid === walletUuid);

    if (!selectedWallet) return;

    setSelectedCurrency(selectedWallet.currency.name as Currencies);
    setSelectedWalletType(selectedWallet.wallet.wallet_type);
    setCurrency(selectedWallet.currency.name as Currencies);
    setWalletId(selectedWallet.wallet.id);
  };

  return {
    business,
    wallet,
    isGlobalAccount,
    selectedCurrency,
    selectedWalletType,
    isCopied,
    values,
    loading,
    instructions,
    currency,
    isAwActive,
    handleOnCurrencyChange,
    copyToClipboard,
  };
};

export default useNewAddFundsModal;
